import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

export const env = createEnv({
  server: {
    CRON_SECRET: z.string().min(1),
    OPENAI_API_KEY: z.string().min(1),
    TURSO_DATABASE_URL: z.string().min(1),
    TURSO_AUTH_TOKEN: z
      .string()
      .optional()
      .refine(
        (token) =>
          Boolean(token) || process.env.TURSO_DATABASE_URL?.startsWith("file:"),
        "Required unless TURSO_DATABASE_URL is a file: URL",
      ),
    NODE_ENV: z.enum(["development", "test", "production"]),
    NEXTAUTH_SECRET: z.string().min(1),
    FIREBASE_OAUTH2_CLIENT_ID: z.string().min(1),
    FIREBASE_OAUTH2_CLIENT_SECRET: z.string().min(1),
    FIREBASE_OAUTH2_REDIRECT_PATH: z.string().min(1),
    GCP_PROJECT_ID: z.string().min(1),
    GCP_KEY_FILE: z.preprocess(
      (base64String) => {
        const decodedString = Buffer.from(base64String, "base64").toString(
          "ascii",
        );
        const jsonObject = JSON.parse(decodedString);
        return jsonObject;
      },
      z.object({
        type: z.string().min(1),
        project_id: z.string().min(1),
        private_key_id: z.string().min(1),
        private_key: z.string().min(1),
        client_email: z.string().min(1),
        client_id: z.string().min(1),
        auth_uri: z.string().min(1),
        token_uri: z.string().min(1),
        auth_provider_x509_cert_url: z.string().min(1),
        client_x509_cert_url: z.string().min(1),
        universe_domain: z.string().min(1),
      }),
    ),
    PROTOCOL: z.string().min(1),
    VERCEL_URL: z.string().min(1),
    SENTRY_AUTH_TOKEN: z.string().min(1),
    BUNDLE_ANALYZER: z
      .string()
      .min(1)
      .transform((val) => val === "true"),
    WITH_SENTRY: z
      .string()
      .min(1)
      .transform((val) => val === "true"),
    HCAPTCHA_SECRET: z.string().min(1),
    ENABLE_TEST_CREDENTIALS_AUTH: z
      .string()
      .optional()
      .transform((val) => val === "true"),
    TEST_CREDENTIALS_PASSWORD: z.string().optional(),
    AUTH_VERIFY_TOKEN_TTL_MINUTES: z
      .string()
      .optional()
      .transform((val) => (val ? Number(val) : 20)),
    AUTH_PASSWORD_PEPPER: z.string().optional(),
    AUTH_PUBLIC_ORIGIN: z.url().optional(),
    FORCE_APPLICATION_PERIOD_OPEN: z.string().optional(),
    /** Shared-drive folder the beslutslista spreadsheets are created in. */
    BESLUTSLISTA_DRIVE_FOLDER_ID: z.string().optional(),
    /** The board's "Beslutslista Mall" spreadsheet, copied for each window. */
    BESLUTSLISTA_TEMPLATE_SPREADSHEET_ID: z.string().optional(),
  },

  client: {
    NEXT_PUBLIC_NODE_ENV: z.enum(["development", "test", "production"]),
    NEXT_PUBLIC_SENTRY_DSN: z.string().min(1),
    NEXT_PUBLIC_VERCEL_URL_PROTOCOL: z.preprocess((str) => {
      if (process.env.NEXT_PUBLIC_VERCEL_URL_PROTOCOL) {
        return process.env.NEXT_PUBLIC_VERCEL_URL_PROTOCOL;
      }
      // Everything below is the fallback for deployments that do not set the
      // value explicitly — preview deployments, whose URL differs every time.
      // It used to reference a bare `PROTOCOL`, which is not a variable here,
      // only a schema key; production never reached it because production sets
      // the value above.
      const protocol = process.env.PROTOCOL ?? "https://";
      if (process.env.DOMAIN) {
        return `${protocol}${process.env.DOMAIN}`;
      }
      // Vercel exposes the per-deployment host under both names for Next.js
      // apps; the public one also survives into the browser bundle.
      const vercelHost =
        process.env.NEXT_PUBLIC_VERCEL_URL ?? process.env.VERCEL_URL;
      if (vercelHost) {
        return `${protocol}${vercelHost}`;
      }
      return str;
    }, z.url()), // This is where the full URL with protocol is validated
    NEXT_PUBLIC_HCAPTCHA_SITE_KEY: z.string().min(1),
  },

  runtimeEnv: {
    CRON_SECRET: process.env.CRON_SECRET,
    OPENAI_API_KEY: process.env.OPENAI_API_KEY,
    TURSO_DATABASE_URL: process.env.TURSO_DATABASE_URL,
    TURSO_AUTH_TOKEN: process.env.TURSO_AUTH_TOKEN,
    // Next.js forces NODE_ENV=production for `next start`; allow an explicit app-level
    // override so we can run production builds in test mode (e2e/CI).
    NODE_ENV: process.env.APP_NODE_ENV ?? process.env.NODE_ENV,
    NEXTAUTH_SECRET: process.env.NEXTAUTH_SECRET,
    PROTOCOL: process.env.PROTOCOL,
    VERCEL_URL: process.env.VERCEL_URL,
    SENTRY_AUTH_TOKEN: process.env.SENTRY_AUTH_TOKEN,
    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
    NEXT_PUBLIC_NODE_ENV:
      process.env.APP_NODE_ENV ??
      process.env.NEXT_PUBLIC_NODE_ENV ??
      process.env.NODE_ENV,
    NEXT_PUBLIC_VERCEL_URL_PROTOCOL:
      process.env.NEXT_PUBLIC_VERCEL_URL_PROTOCOL,
    FIREBASE_OAUTH2_CLIENT_ID: process.env.FIREBASE_OAUTH2_CLIENT_ID,
    FIREBASE_OAUTH2_CLIENT_SECRET: process.env.FIREBASE_OAUTH2_CLIENT_SECRET,
    FIREBASE_OAUTH2_REDIRECT_PATH: process.env.FIREBASE_OAUTH2_REDIRECT_PATH,
    GCP_PROJECT_ID: process.env.GCP_PROJECT_ID,
    GCP_KEY_FILE: process.env.GCP_KEY_FILE,
    BUNDLE_ANALYZER: process.env.BUNDLE_ANALYZER,
    WITH_SENTRY: process.env.WITH_SENTRY,
    HCAPTCHA_SECRET: process.env.HCAPTCHA_SECRET,
    NEXT_PUBLIC_HCAPTCHA_SITE_KEY: process.env.NEXT_PUBLIC_HCAPTCHA_SITE_KEY,
    ENABLE_TEST_CREDENTIALS_AUTH: process.env.ENABLE_TEST_CREDENTIALS_AUTH,
    TEST_CREDENTIALS_PASSWORD: process.env.TEST_CREDENTIALS_PASSWORD,
    AUTH_VERIFY_TOKEN_TTL_MINUTES: process.env.AUTH_VERIFY_TOKEN_TTL_MINUTES,
    AUTH_PASSWORD_PEPPER: process.env.AUTH_PASSWORD_PEPPER,
    AUTH_PUBLIC_ORIGIN: process.env.AUTH_PUBLIC_ORIGIN,
    FORCE_APPLICATION_PERIOD_OPEN: process.env.FORCE_APPLICATION_PERIOD_OPEN,
    BESLUTSLISTA_DRIVE_FOLDER_ID: process.env.BESLUTSLISTA_DRIVE_FOLDER_ID,
    BESLUTSLISTA_TEMPLATE_SPREADSHEET_ID:
      process.env.BESLUTSLISTA_TEMPLATE_SPREADSHEET_ID,
  },

  skipValidation: process.env.SKIP_ENV_VALIDATION === "true",
});
